import React, { useState, useEffect } from 'react'
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { AdditionalComponentItem } from './AdditionalComponentItem'
import { Badge } from '@/components/ui/badge'
import { GET_ADDITIONAL_COMPONENT_GROUPS } from '@/containers/AdditionalComponentGroups/additionalComponentGroupQueries'
import {
  ADD_COMPONENT_GROUP_TO_PRODUCT,
  GET_PRODUCT_DETAILS,
} from '@/containers/Products/productQueries'
import {
  GET_ADDITIONAL_COMPONENTS_BY_GROUP,
  ADD_COMPONENT_TO_PRODUCT,
} from '@/components/queries/additionalComponentQueries'
import { Plus, Loader2 } from 'lucide-react'

const GET_SOFA_FORMS = gql`
  query GetSofaForms($productId: Int!) {
    findUniqueAdvancedProduct(where: { id: $productId }) {
      id
      sofa_forms {
        metadata
      }
    }
  }
`

interface AdditionalComponentsCardProps {
  advancedProduct: any
  language: string
  manufacturerId: number
  containerProductId: number
}

export function AdditionalComponentsCard({
  advancedProduct,
  language,
  manufacturerId,
  containerProductId,
}: AdditionalComponentsCardProps) {
  const [moduleCompatibilityCounts, setModuleCompatibilityCounts] = useState<
    Record<number, number>
  >({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)

  // State for adding components to existing groups
  const [isAddComponentsModalOpen, setIsAddComponentsModalOpen] =
    useState(false)
  const [targetGroupId, setTargetGroupId] = useState<number | null>(null)
  const [selectedComponentIds, setSelectedComponentIds] = useState<number[]>([])

  const [loadSofaForms, { data: sofaFormsData }] = useLazyQuery(
    GET_SOFA_FORMS,
    {
      fetchPolicy: 'cache-first',
    }
  )

  // Query to get all available component groups
  const { data: componentGroupsData, loading: loadingGroups } = useQuery(
    GET_ADDITIONAL_COMPONENT_GROUPS,
    {
      variables: {
        take: 1000, // Get all groups
        skip: 0,
      },
      fetchPolicy: 'cache-first',
    }
  )

  // Mutation to add component group to product
  const [addComponentGroupToProduct, { loading: addingGroup }] = useMutation(
    ADD_COMPONENT_GROUP_TO_PRODUCT,
    {
      refetchQueries: [
        {
          query: GET_PRODUCT_DETAILS,
          variables: { id: containerProductId },
        },
      ],
      onCompleted: () => {
        setIsModalOpen(false)
        setSelectedGroupId(null)
      },
      onError: error => {
        console.error('Error adding component group:', error)
      },
    }
  )

  // Query to get available components for a specific group
  const [
    loadAvailableComponents,
    { data: availableComponentsData, loading: loadingComponents },
  ] = useLazyQuery(GET_ADDITIONAL_COMPONENTS_BY_GROUP, {
    fetchPolicy: 'cache-and-network',
  })

  // Mutation to add individual component to product
  const [addComponentToProduct] = useMutation(ADD_COMPONENT_TO_PRODUCT, {
    refetchQueries: [
      {
        query: GET_PRODUCT_DETAILS,
        variables: { id: containerProductId },
      },
    ],
  })

  // State for tracking the adding process
  const [addingComponents, setAddingComponents] = useState(false)

  // State for tracking expanded accordion items
  const [expandedAccordionValue, setExpandedAccordionValue] =
    useState<string>('')

  // Load sofa forms data when component mounts
  useEffect(() => {
    if (advancedProduct?.id) {
      loadSofaForms({
        variables: {
          productId: advancedProduct.id,
        },
      })
    }
  }, [advancedProduct?.id, loadSofaForms])

  // Process sofa forms data to calculate compatibility counts
  useEffect(() => {
    if (sofaFormsData?.findUniqueAdvancedProduct?.sofa_forms) {
      const sofaForms = sofaFormsData.findUniqueAdvancedProduct.sofa_forms
      const compatibilityCounts: Record<number, number> = {}

      // Process each sofa form's metadata
      sofaForms.forEach((form: any) => {
        try {
          const metadata =
            typeof form.metadata === 'string'
              ? JSON.parse(form.metadata)
              : form.metadata

          // Check if armrestsConditions exists in metadata
          if (
            metadata?.armrestsConditions &&
            Array.isArray(metadata.armrestsConditions)
          ) {
            // armrestsConditions is an array of component codes like ["ALPINE:A001"]
            // Find all armrest components
            const armrestComponents =
              advancedProduct.additional_component_to_advanced_product.filter(
                (comp: any) => {
                  const groupConnection =
                    advancedProduct.additional_component_group_to_advanced_product.find(
                      (group: any) =>
                        group.additional_component_group.id ===
                        comp.additional_component.additionalComponentGroupId
                    )
                  return (
                    groupConnection &&
                    groupConnection.additional_component_group.code ===
                      'armrest'
                  )
                }
              )

            // Check which armrests are in the conditions
            armrestComponents.forEach((comp: any) => {
              const componentCode = comp.additional_component.code
              if (metadata.armrestsConditions.includes(componentCode)) {
                const id = comp.additional_component.id
                compatibilityCounts[id] = (compatibilityCounts[id] || 0) + 1
              }
            })
          }
        } catch (error) {
          console.error('Error processing sofa form metadata:', error)
        }
      })

      setModuleCompatibilityCounts(compatibilityCounts)
    }
  }, [sofaFormsData, advancedProduct])

  // Get available component groups (not already assigned to product)
  const availableGroups =
    componentGroupsData?.findManyAdditionalComponentGroup?.filter(
      (group: any) => {
        const isAlreadyAssigned =
          advancedProduct.additional_component_group_to_advanced_product?.some(
            (assigned: any) =>
              assigned.additional_component_group.id === group.id
          )
        return !isAlreadyAssigned
      }
    ) || []

  // Handle adding component group to product
  const handleAddComponentGroup = async () => {
    if (!selectedGroupId) return

    try {
      await addComponentGroupToProduct({
        variables: {
          advancedProductId: advancedProduct.id,
          additionalComponentGroupId: selectedGroupId,
          enabled: true,
        },
      })
    } catch (error) {
      console.error('Failed to add component group:', error)
    }
  }

  // Handle opening add components modal
  const handleOpenAddComponentsModal = (groupId: number) => {
    setTargetGroupId(groupId)
    setSelectedComponentIds([])
    setIsAddComponentsModalOpen(true)

    // Load available components for this group
    loadAvailableComponents({
      variables: {
        groupId: groupId,
        advancedProductId: advancedProduct.id,
      },
    })
  }

  // Handle component selection
  const handleComponentSelection = (componentId: number, checked: boolean) => {
    setSelectedComponentIds(prev => {
      if (checked) {
        return [...prev, componentId]
      } else {
        return prev.filter(id => id !== componentId)
      }
    })
  }

  // Handle adding selected components to product
  const handleAddComponents = async () => {
    if (!targetGroupId || selectedComponentIds.length === 0) return

    setAddingComponents(true)

    try {
      // Make individual calls for each component
      const promises = selectedComponentIds.map(componentId =>
        addComponentToProduct({
          variables: {
            advancedProductId: advancedProduct.id,
            additionalComponentId: componentId,
            enabled: true,
          },
        })
      )

      await Promise.all(promises)

      // Close modal and reset state
      setIsAddComponentsModalOpen(false)
      setTargetGroupId(null)
      setSelectedComponentIds([])
    } catch (error) {
      console.error('Failed to add components:', error)
    } finally {
      setAddingComponents(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional Components</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Accordion
          type="single"
          collapsible
          className="w-full"
          value={expandedAccordionValue}
          onValueChange={setExpandedAccordionValue}
        >
          {advancedProduct.additional_component_group_to_advanced_product?.map(
            (groupConnection: any) => (
              <AccordionItem
                key={groupConnection.id}
                value={`additional-component-group=${groupConnection.id}`}
              >
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <h4
                      className={`font-semibold ${
                        !groupConnection.enabled ? 'text-gray-400' : ''
                      }`}
                    >
                      {groupConnection.additional_component_group.additional_component_group_profiles.find(
                        (profile: any) =>
                          profile.language.toLowerCase() === language
                      )?.name ||
                        groupConnection.additional_component_group
                          .additional_component_group_profiles[0]?.name}
                    </h4>
                    {groupConnection.additional_component_group
                      .use_fabric_prices_for_components && (
                      <Badge variant="outline">Fabric Prices</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {/* Add Component Button - only visible when expanded */}
                  <div className="mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleOpenAddComponentsModal(
                          groupConnection.additional_component_group.id
                        )
                      }
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Component
                    </Button>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    {/* Table header */}
                    <div className="flex items-center py-3 px-4 border-b font-medium text-xs text-gray-500">
                      <div className="w-[60px]">Image</div>
                      <div className="flex-1">Name</div>
                      <div className="w-[180px]">Code</div>
                      <div className="w-[100px]">Status</div>
                      <div className="w-[100px] ">Rules</div>
                      <div className="w-[100px] text-right"></div>
                    </div>

                    {/* Table content */}
                    <div>
                      {advancedProduct.additional_component_to_advanced_product
                        .filter(
                          (comp: any) =>
                            comp.additional_component
                              .additionalComponentGroupId ===
                            groupConnection.additional_component_group.id
                        )
                        .map((componentConnection: any) => (
                          <AdditionalComponentItem
                            key={componentConnection.additional_component.id}
                            componentConnection={{
                              ...componentConnection,
                              component_group_id:
                                groupConnection.additional_component_group.id,
                            }}
                            language={language}
                            advancedProductId={advancedProduct.id}
                            manufacturerId={manufacturerId}
                            componentGroup={
                              groupConnection.additional_component_group
                            }
                            moduleCompatibilityCount={
                              groupConnection.additional_component_group
                                .code === 'armrest'
                                ? moduleCompatibilityCounts[
                                    componentConnection.additional_component.id
                                  ] || 0
                                : undefined
                            }
                            totalModulesCount={
                              sofaFormsData?.findUniqueProductContainer
                                ?.advanced_product?.sofa_forms?.length || 0
                            }
                          />
                        ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )
          )}
        </Accordion>

        {/* Add Component Group Button */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full mt-4"
              disabled={loadingGroups || availableGroups.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add component group
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Component Group</DialogTitle>
              <DialogDescription>
                Select a component group to add to this product.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {loadingGroups ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading groups...</span>
                </div>
              ) : availableGroups.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No additional component groups available
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableGroups.map((group: any) => (
                    <div
                      key={group.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedGroupId === group.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedGroupId(group.id)}
                    >
                      <div className="font-medium">
                        {group.additional_component_group_profiles.find(
                          (profile: any) =>
                            profile.language.toLowerCase() === language
                        )?.name ||
                          group.additional_component_group_profiles[0]?.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        Code: {group.code}
                      </div>
                      {group.use_fabric_prices_for_components && (
                        <Badge variant="outline" className="mt-1">
                          Fabric Prices
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsModalOpen(false)
                  setSelectedGroupId(null)
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddComponentGroup}
                disabled={!selectedGroupId || addingGroup}
              >
                {addingGroup && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Add Group
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Components to Group Modal */}
        <Dialog
          open={isAddComponentsModalOpen}
          onOpenChange={setIsAddComponentsModalOpen}
        >
          <DialogContent className="sm:max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Add Components to Group</DialogTitle>
              <DialogDescription>
                Select components to add to this product. Only components that
                are not currently assigned to this product are shown.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {loadingComponents ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading components...</span>
                </div>
              ) : !availableComponentsData?.findManyAdditionalComponent ||
                availableComponentsData.findManyAdditionalComponent.length ===
                  0 ? (
                <div className="text-center py-8  text-sm text-gray-400">
                  All components in this group are already assigned to this
                  product.
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {availableComponentsData.findManyAdditionalComponent.map(
                    (component: any) => (
                      <div
                        key={component.id}
                        className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <Checkbox
                          id={`component-${component.id}`}
                          checked={selectedComponentIds.includes(component.id)}
                          onCheckedChange={checked =>
                            handleComponentSelection(component.id, !!checked)
                          }
                        />
                        <div className="flex items-center space-x-3 flex-1">
                          {component.image?.src_thumbnail && (
                            <img
                              src={component.image.src_thumbnail}
                              alt=""
                              className="w-12 h-12 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <div className="font-medium">
                              {component.additional_component_profiles.find(
                                (profile: any) =>
                                  profile.language.toLowerCase() === language
                              )?.name ||
                                component.additional_component_profiles[0]
                                  ?.name ||
                                'Unnamed Component'}
                            </div>
                            <div className="text-sm text-gray-500">
                              Code: {component.code}
                            </div>
                            {component.component_sku && (
                              <div className="text-sm text-gray-500">
                                SKU: {component.component_sku}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <div className="flex items-center justify-between w-full">
                <div className="text-sm text-gray-500">
                  {selectedComponentIds.length} component
                  {selectedComponentIds.length !== 1 ? 's' : ''} selected
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsAddComponentsModalOpen(false)
                      setTargetGroupId(null)
                      setSelectedComponentIds([])
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddComponents}
                    disabled={
                      selectedComponentIds.length === 0 || addingComponents
                    }
                  >
                    {addingComponents && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Add {selectedComponentIds.length} Component
                    {selectedComponentIds.length !== 1 ? 's' : ''}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}
